/**
 * <park-admin-caption> — the labelled caption/alt-text editor (anton/forrest#1248),
 * lifted 1:1 from `.gm-caplabel` + its `<textarea class="gm-caption">` in
 * `components/admin/GalleryManager.astro` (anton/forrest#674/anton/forrest#774),
 * where it recurred TWICE on the same surface: the add-photo form (a fresh
 * caption before the photo is created) and the per-row per-locale editor (an
 * existing caption, its aria-label swapped as the locale tab changes). Two
 * private copies of the same "small uppercase label above a resizable
 * textarea" pattern is exactly what `design-system#FR-3` / `admin-shell#FR-8`
 * forbid: FR-8 requires every input to carry a *visible* text name, and a
 * hand-rolled copy per call site is how that requirement silently drifts
 * (e.g. one copy losing its `<label>` wrapping and falling back to a bare
 * `aria-label`).
 *
 * Renders an ACTUAL `<label>` wrapping the head text and the textarea — not
 * just an `aria-label` — so the visible/programmatic name stay the same text
 * by construction (FR-8's "visible text name", not merely an accessible name).
 *
 * Unlike `<park-admin-status>`, this element's textarea VALUE is driven
 * imperatively by the page's own client script after first render (locale
 * switch swaps `.pa-caption-field.value` directly, exactly as the old
 * `.gm-caption` did) — `value`/`field-aria-label` only seed the two contexts
 * that DO go through attributes: the initial SSR render and the Claude-Design
 * preview. `field-id` lets the page's script keep addressing the textarea by
 * `getElementById` exactly as before (the id now lands on the INNER
 * `<textarea>`, not on this wrapper, since the wrapper itself has no `.value`).
 *
 * Author syntax:
 *   <park-admin-caption
 *     label="Kuvateksti / alt-teksti"
 *     placeholder="Kuvaile kuva lyhyesti…"
 *     field-aria-label="Kuvateksti — rivi 1"
 *     value="Metsä aamulla"
 *   ></park-admin-caption>
 *
 * Styling lives in wc/css/park-admin-caption.css — no <style> here, no shadow DOM.
 *
 * @dsSpec admin-shell#FR-8 — every input carries a visible text name.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-caption";

export interface ParkAdminCaptionData {
	label?: string;
	placeholder?: string;
	fieldId?: string;
	fieldAriaLabel?: string;
	rows?: string | number;
	value?: string;
}

/**
 * Pure render: props → inner HTML string (a `<label>` wrapping the head span
 * and the textarea). Safe to call server-side and client-side. `value` is
 * rendered as the textarea's initial text content (not a `value=` attribute —
 * textareas hold their value as content), matching how the original
 * `.gm-caption` markup worked so per-row server-rendered captions survive
 * no-JS.
 */
export function renderParkAdminCaption(data: ParkAdminCaptionData): string {
	const id = data.fieldId ? html` id="${esc(data.fieldId)}"` : "";
	const ariaLabel = data.fieldAriaLabel
		? html` aria-label="${esc(data.fieldAriaLabel)}"`
		: "";
	const placeholder = data.placeholder
		? html` placeholder="${esc(data.placeholder)}"`
		: "";
	const rows = esc(data.rows ?? 2);
	return html`<label class="pa-caption"><span class="pa-caption-head">${esc(data.label)}</span><textarea class="pa-caption-field" rows="${rows}"${id}${ariaLabel}${placeholder}>${esc(data.value)}</textarea></label>`;
}

class ParkAdminCaption extends ParkElement {
	static get observedAttributes(): string[] {
		return [
			"label",
			"placeholder",
			"field-id",
			"field-aria-label",
			"rows",
			"value",
		];
	}

	protected render(): string {
		return renderParkAdminCaption({
			label: this.attr("label"),
			placeholder: this.attr("placeholder"),
			fieldId: this.attr("field-id"),
			fieldAriaLabel: this.attr("field-aria-label"),
			rows: this.attr("rows"),
			value: this.attr("value"),
		});
	}
}

defineElement(TAG, ParkAdminCaption);

export { ParkAdminCaption };
